import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pricingApi, type Promotion, type Coupon, type CouponValidation } from '../../api/pricing';

export function PromotionManagerPage() {
  const [items, setItems] = useState<Promotion[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const reload = () => void pricingApi.listPromotions().then(setItems).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!code.trim() || !name.trim()) return;
    try {
      await pricingApi.createPromotion({ code: code.trim(), name: name.trim(), promotion_type: 'flash_sale' });
      setCode('');
      setName('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Promotion Manager</h1>
      <div className="mt-4 flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create promotion</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th></tr></thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-b">
              <td className="px-4 py-3 font-medium">{p.name}</td>
              <td className="px-4 py-3">{p.promotion_type}</td>
              <td className="px-4 py-3">{p.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DiscountManagerPage() {
  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Discount Manager</h1>
      <p className="mt-2 text-sm text-slate-600">Discount rules are applied via promotions and coupons. Create percentage, fixed, tier, and bundle discounts through the promotion engine.</p>
      <Link to="/pricing/promotions" className="mt-4 inline-block text-sm text-slate-700 hover:underline">Manage promotions →</Link>
    </div>
  );
}

export function CouponManagerPage() {
  const [items, setItems] = useState<Coupon[]>([]);
  const [code, setCode] = useState('');
  const [value, setValue] = useState('10');
  const [validateCode, setValidateCode] = useState('');
  const [validation, setValidation] = useState<CouponValidation | null>(null);
  const [error, setError] = useState('');

  const reload = () => void pricingApi.listCoupons().then(setItems).catch((e) => setError(String(e)));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!code.trim()) return;
    try {
      await pricingApi.createCoupon({ code: code.trim(), discount_type: 'percent', value: parseFloat(value) || 10 });
      setCode('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  const validate = async () => {
    if (!validateCode.trim()) return;
    try {
      const r = await pricingApi.validateCoupon({ code: validateCode.trim(), order_amount: 1000 });
      setValidation(r);
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/pricing" className="text-sm text-slate-600 hover:underline">← Pricing hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Coupon Manager</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Coupon code" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Discount %" className="w-24 rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create coupon</button>
      </div>
      <div className="mt-4 flex gap-2">
        <input value={validateCode} onChange={(e) => setValidateCode(e.target.value)} placeholder="Validate code" className="rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void validate()} className="rounded-lg border px-4 py-2 text-sm">Validate</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {validation && <p className="mt-4 text-sm">{validation.valid ? `Valid — discount ${validation.discount_amount}` : validation.message ?? 'Invalid'}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Value</th><th className="px-4 py-3">Status</th></tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b">
              <td className="px-4 py-3 font-mono">{c.code}</td>
              <td className="px-4 py-3">{c.discount_type}</td>
              <td className="px-4 py-3">{c.value}</td>
              <td className="px-4 py-3">{c.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
